import React, { useCallback } from "react"
import styled, { useTheme } from "styled-components"
import Hamburger from "@components/hamburger"
import PropTypes from "prop-types"
import Button from "@components/elements/button"
import media from "@styles/media"
import { ReactComponent as KlipfolioLogo } from "@images/klipfolio-logo.svg"
import {
  NavbarLinkWrapper,
  NavbarDropdownColumn,
  NavbarVerticalEntry,
  NavbarSeperator,
  NavbarLinkText,
} from "./styles"
import Flex from "@components/elements/flex"
import Anchor from "@components/elements/anchor"
import { LinkType } from "@types/link-type"
import { DesktopResourcesMenu, MobileResourcesMenu } from "./resources"
import Container from "@components/layout/container"
import { DesktopProductsMenu, MobileProductsMenu } from "./products"
import { PRODUCTS, useProduct } from "@hooks/use-product"
import { useBanner } from "@hooks/use-banner"
import {
  motion,
  useScroll,
  useTransform,
  useMotionTemplate,
} from "framer-motion"
import { MotionFlex, MotionGrid } from "@components/elements/motion"
import { SubNavType } from "@types/sub-nav-type"
import useDebouncedState from "@hooks/use-debounced-state"
import { ReactComponent as Chevron } from "@images/chevron.svg"

const NavbarLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-area: ${({ area }) => area};
  ${media.md`
    display: none;
  `}
`

const NavWrapper = styled(Container)`
  padding: 0 15px;
  display: flex;
  flex-flow: column;
  align-items: center;
  position: relative;
  z-index: 100;
  top: var(--nav-top, 46px);
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    height: 100%;
  }
  ${media.md`
    & > div {
      height: 100%;
    }
  `}

  #mobile-demo {
    display: none;
  }
  #mobile-full {
    display: none;
  }
  #desktop-full {
    display: flex;
  }

  ${media.md`
  
    top: 0 !important;
    `}

  ${media.sm`  
    #desktop-demo {
      display: none;
    }
    #mobile-demo {
      display: block;
    }
    #desktop-full {
      display: none;
    }
    #mobile-full {
      display: flex;
    }
  `}
`

const MotionNavWrapper = motion(NavWrapper)

const Nav = ({ children }) => {
  const { banner } = useBanner()
  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 46], ["46px", "0px"])

  return (
    <MotionNavWrapper as="nav" style={{ "--nav-top": banner ? y : "0px" }}>
      {children}
    </MotionNavWrapper>
  )
}

Nav.propTypes = {
  children: PropTypes.node,
}

const MenuLink = ({ name, link, ...rest }) => {
  const { color } = useTheme()
  return (
    <Anchor
      fontSize="0.9rem"
      color="#333"
      hover={color.blue400}
      link={link}
      style={{
        padding: "18px 12px",
      }}
      {...rest}
    >
      {name}
    </Anchor>
  )
}

MenuLink.propTypes = {
  name: PropTypes.string,
  link: PropTypes.string,
}

const CtaButton = ({ product }) => {
  if (product === PRODUCTS.POWERMETRICS) {
    return (
      <Button.PowerMetricsSimple
        notice={false}
        arrow={false}
        padding="0.75rem 1.25rem"
        lineHeight="1"
        data-cta-location="menu"
        force
      >
        Get Started Free
      </Button.PowerMetricsSimple>
    )
  } else {
    return (
      <Button.KlipsSimple
        notice={false}
        arrow={false}
        padding="0.75rem 1.25rem"
        lineHeight="1"
        color={"white"}
        data-cta-location="menu"
      >
        Get Started Now
      </Button.KlipsSimple>
    )
  }
}

CtaButton.propTypes = {
  product: PropTypes.number,
}

const SubNavDropdownWrapper = styled.div`
  padding: 0 0.5rem;
  position: relative;
  display: inline-block;
`

const SubNavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.color.blue600};
  cursor: pointer;
  transition: opacity 0.2s ease;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0.5)};

  svg {
    transition: transform 0.2s ease;
    transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0)")};
  }
`

const SubNavDropdownMenu = styled(Flex)`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 200px;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  transform: ${({ isOpen }) =>
    isOpen ? "translateY(0)" : "translateY(-10px)"};
  transition: all 0.2s ease;
  z-index: 1000;
  padding: 1rem 1.5rem;
`

const SubNavDropdown = ({ category, links }) => {
  const { color } = useTheme()

  const [isHovering, setIsHovering] = useDebouncedState(false, 100)

  const handleMouseOver = useCallback(() => {
    setIsHovering(true)
  }, [setIsHovering])

  const handleMouseOut = useCallback(() => {
    setIsHovering(false)
  }, [setIsHovering])

  return (
    <SubNavDropdownWrapper>
      <Flex flexFlow="row" alignItems="center" gap="0.5rem">
        <Anchor
          fontWeight={600}
          color={color.blue600}
          hover={color.blue500}
          href={category.href}
        >
          {category.name}
        </Anchor>
        <Chevron
          width={9}
          height={7}
          style={{ marginLeft: "2px", transform: "rotate(270deg)" }}
        />
        <Flex
          position="relative"
          onMouseEnter={handleMouseOver}
          onMouseLeave={handleMouseOut}
        >
          <SubNavButton isOpen={isHovering}>{links[0].name}</SubNavButton>
          <SubNavDropdownMenu flexFlow="column" isOpen={isHovering}>
            {links.map((link, i) => (
              <Anchor key={`subnav-link-${i}`} href={link.href}>
                {link.name}
              </Anchor>
            ))}
          </SubNavDropdownMenu>
        </Flex>
      </Flex>
    </SubNavDropdownWrapper>
  )
}

SubNavDropdown.propTypes = {
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  }).isRequired,
  links: PropTypes.arrayOf(LinkType).isRequired,
}

export const Menu = ({
  mobileState,
  setMobileState,
  landingPage,
  links,
  productOverride,
  login,
  subNav,
  flat,
}) => {
  const { shadow } = useTheme()
  const { product } = useProduct()
  const ctaProduct = productOverride ?? product
  const { scrollY } = useScroll()
  const shadowOpacity = useTransform(scrollY, [0, 200], [0, 0.2])
  const padding = useTransform(scrollY, [0, 200], ["0 0", "0 .5rem"])
  const paddingSubnav = useTransform(scrollY, [0, 200], ["0 0", "0.5rem .5rem"])
  const backgroundSubnav = useTransform(
    scrollY,
    [0, 200],
    ["rgba(246, 249, 250, 0)", "rgba(246, 249, 250, 1)"]
  )
  const animatedShadow = useMotionTemplate`0px 0px 30px rgba(0, 0, 0, ${shadowOpacity})`
  const caretTranslate = subNav
    ? useTransform(scrollY, [0, 200], ["29px", "45px"])
    : "-5px"

  return (
    <Nav>
      <MotionFlex
        width="100%"
        height="100%"
        borderRadius={subNav ? "1rem" : "2rem"}
        background="white"
        style={{
          boxShadow: subNav || flat ? animatedShadow : shadow.subtle,
        }}
        zIndex={11}
      >
        <MotionGrid
          columns="1fr 2fr 1fr"
          areas="logo links cta"
          alignItems="center"
          width="100%"
          height="60px"
          zIndex={11}
          style={{
            padding: subNav ? padding : "0 1rem",
          }}
        >
          <a href={"/"} style={{ marginLeft: "10px", display: "flex" }}>
            <KlipfolioLogo />
          </a>
          <Flex flex="1"></Flex>
          <Hamburger
            onClick={() => setMobileState(!mobileState)}
            state={mobileState}
          ></Hamburger>
          {!landingPage && (
            <NavbarLinks area="links">
              <DesktopProductsMenu caretTranslate={caretTranslate} />
              <MenuLink name="Pricing" link="/pricing" />
              <DesktopResourcesMenu caretTranslate={caretTranslate} />
              {links?.map((link, i) => (
                <NavbarLinkWrapper key={`MenuDesktopLinksWrapper-${i}`}>
                  <NavbarLinkText
                    id={`linkswrapper-${i}`}
                    hasDropdown={false}
                    title={link.name}
                    as={"a"}
                    href={link.link}
                  >
                    {link.name}
                  </NavbarLinkText>
                </NavbarLinkWrapper>
              ))}
            </NavbarLinks>
          )}
          <Flex
            alignItems="center"
            justifyContent="right"
            justifySelf="flex-end"
            width="max-content"
            margin="0"
            area="cta"
            gap="0.25rem"
            flexFlow="row"
            hideOnMd
          >
            {login && (
              <NavbarLinkText
                title={"Log In"}
                className="sign-in-link"
                as={"a"}
                href={"https://app.klipfolio.com/login"}
              >
                Log In
              </NavbarLinkText>
            )}

            <CtaButton product={ctaProduct} />
          </Flex>
        </MotionGrid>
        {subNav && (
          <MotionFlex
            style={{
              padding: paddingSubnav,
              background: backgroundSubnav,
            }}
            width="100%"
            justifyContent="space-between"
            borderRadius="0 0 1rem 1rem"
            hideOnSm
          >
            <SubNavDropdown category={subNav.category} links={subNav.links} />
          </MotionFlex>
        )}
      </MotionFlex>
      <MobileMenu mobileState={mobileState} />
    </Nav>
  )
}

Menu.propTypes = {
  mobileState: PropTypes.bool,
  setMobileState: PropTypes.func.isRequired,
  landingPage: PropTypes.bool,
  links: PropTypes.arrayOf(LinkType),
  productOverride: PropTypes.number,
  login: PropTypes.bool,
  subNav: SubNavType,
  flat: PropTypes.bool,
}

const MobileNav = styled.div`
  position: fixed;
  top: 0;
  transform: ${props => (props.isOpen ? "translateX(0)" : "translateX(100%)")};
  transition: transform 0.5s;
  left: 0;
  right: 0;
  height: 100%;
  background: white;
  overflow-y: scroll;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10;
  ul {
    margin: 0;
    list-style: none;
  }

  ${NavbarSeperator} {
    margin: 2rem 0;
  }

  ${NavbarDropdownColumn} {
    span {
      margin: 0;
    }

    ${NavbarDropdownColumn} {
      margin-left: 0.5rem;
    }
  }
`

const MobileNavbarLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column;
  height: max-content;
  display: flex;
  padding: 6rem 1rem 0 1rem;
  ${media.sm_up`
    max-width: 690px;
    grid-template-columns: 1fr;
  `}

  ${NavbarLinkWrapper} {
    width: 100%;
  }

  ${NavbarVerticalEntry} {
    margin-top: 0;
  }

  ${media.sm`
    width: 100%;
  `}
`

const MobileMenu = ({ mobileState }) => {
  return (
    <MobileNav isOpen={mobileState}>
      <MobileNavbarLinks>
        <Anchor
          className="sign-in-link"
          link={"https://app.klipfolio.com/login"}
          arrow
          style={{
            alignSelf: "start",
            marginBottom: "1rem",
          }}
        >
          Log In
        </Anchor>
        <Flex gap="2rem">
          <MobileProductsMenu />
          <MenuLink
            name="Pricing"
            link="/pricing"
            style={{
              padding: 0,
            }}
            fontSize="1rem"
            arrow
          />
          <MobileResourcesMenu />
        </Flex>
      </MobileNavbarLinks>
    </MobileNav>
  )
}

MobileMenu.propTypes = {
  mobileState: PropTypes.bool,
}
